import React, { useState } from 'react';
import { 
    CommandBar, 
    ICommandBarItemProps,
    ICommandBarStyles,
    getTheme,
    mergeStyleSets,
    mergeStyles,
} from '@fluentui/react';
import WorkCreateComponent from '../work/work-create.component';
import LoaderComponent from '../loader/loader.component';
import WorkPartCreateComponent from '../work-part/work-part-create.component';
import WorkEditComponent from '../work/work-edit.component';
import WorkDocumentCreateComponent from '../work-document/work-document-create.component';
import WorkCobrosComponent from '../work-cobros/work-cobros.component';
import WorkConsumosComponent from '../work-consumos/work-consumos.component';
import NoPermissionComponent from '../no-permission/no-permission.component';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';
import moment from 'moment';

import ItemOrdenDeTrabajo from '../../models/ItemOrdenDeTrabajo';
import ItemTarea from '../../models/ItemTarea';
import ItemPermiso from '../../models/ItemPermiso';
import ElementEnum from '../../enums/ElementEnum';

const theme = getTheme();

const listClasses = mergeStyleSets({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '4px 0',
    },
    card: {
        display: 'flex',
        backgroundColor: '#ffffff',
        borderRadius: 10,
        boxShadow: '0 1px 5px rgba(0,0,0,0.07)',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, background 0.15s',
        selectors: {
            ':hover': {
                boxShadow: '0 3px 12px rgba(0,0,0,0.13)',
                backgroundColor: '#fff8f7',
            },
        },
    },
    cardSelected: {
        display: 'flex',
        backgroundColor: '#fff3f2',
        borderRadius: 10,
        boxShadow: '0 0 0 2px var(--color-primario)',
        overflow: 'hidden',
        cursor: 'pointer',
    },
    sidebar: {
        width: 5,
        flexShrink: 0,
    },
    cardInner: {
        flex: 1,
        minWidth: 0,
        padding: '10px 12px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
    },
    badge: {
        flexShrink: 0,
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: 'var(--color-primario)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 700,
        fontSize: 11,
        marginTop: 2,
    },
    content: {
        flex: 1,
        overflow: 'hidden',
        minWidth: 0,
    },
    title: {
        fontWeight: 600,
        color: '#1a1a1a',
        fontSize: 13,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: 2,
    },
    subtitle: {
        fontSize: 12,
        color: '#555',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    obs: {
        fontSize: 11,
        color: '#999',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginTop: 2,
    },
    dateCol: {
        flexShrink: 0,
        fontSize: 10,
        color: '#aaa',
        textAlign: 'right' as const,
        paddingTop: 2,
        minWidth: 70,
    },
    emptyMsg: {
        padding: 16,
        color: '#aaa',
        fontSize: 13,
        textAlign: 'center' as const,
    },
});

const commandBarStyles: ICommandBarStyles = {
    root: {
        selectors: {
            '.ms-Icon': {
                color:'var(--color-primario)',
            },
            '&:hover': {
                selectors: {
                    '.ms-Icon': {
                        color: 'var(--color-primario)',
                    },
                },
            },
        },
    },
};

interface IWorkOrderWorksComponentProps {
    workOrder: ItemOrdenDeTrabajo;
    onWorkCreated: () => void;
    onWorkEdited: () => void;
    onWorkDeleted: () => void;
    onWorkPartCreated: () => void;
    onWorkDocumentCreated: () => void;
};

const WorkOrderWorksComponent = (props: IWorkOrderWorksComponentProps) => {
    const auth = useAuthStatus();
    const workPermission : ItemPermiso = auth.getPermission(ElementEnum.WORKS);

    const [work, setWork] = useState<ItemTarea>();
    const [isWorkCreateFormOpen, setIsWorkCreateFormOpen] = useState<boolean>(false);
    const [isWorkEditFormOpen, setIsWorkEditFormOpen] = useState<boolean>(false);
    const [isWorkPartFormOpen, setIsWorkPartFormOpen] = useState<boolean>(false);
    const [isWorkDocumentOpen, setIsWorkDocumentOpen] = useState<boolean>(false);
    const [isCobrosOpen, setIsCobrosOpen] = useState<boolean>(false);
    const [isConsumosOpen, setIsConsumosOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const commandBarItems : ICommandBarItemProps[] = [];
    if (workPermission.Nuevo) {
        commandBarItems.push(
            {
                key: '0',
                text: 'Nuevo',
                iconProps: {
                    iconName: 'Add',
                },
                onClick: () => {
                    setIsWorkCreateFormOpen(true);
                },
            },
        );
    }
    if (workPermission.Modificar) {
        commandBarItems.push(
            {
                key: '1',
                text: 'Editar',
                iconProps: {
                    iconName: 'Edit',
                },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para editarlo.");
                    }
                    else {
                        setIsWorkEditFormOpen(true);
                    }
                },
            },
            {
                key: '2',
                text: 'Cerrar',
                iconProps: {
                    iconName: 'ChromeClose',
                },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para cerrarlo.");
                    }
                    else {
                        if (auth.user) {
                            setLoading(true);
                            ApiService.Post<string>("OrdenesTrabajo", "SetCerrarTrabajo", auth.user, {
                                "pItemTrabajo": work,
                            }).then((response: string) => {
                                alert(response);
                                setLoading(false);
                            }, (error: string) => {
                                alert(error);
                                setLoading(false);
                            });
                        }
                    }
                },
            },
        )
    }
    if (workPermission.Eliminar) {
        commandBarItems.push(
            {
                key: '3',
                text: 'Borrar',
                iconProps: {
                    iconName: 'Delete',
                },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para eliminarlo.");
                    }
                    else {
                        if (auth.user) {
                            setLoading(true);
                            ApiService.Post<void>("OrdenesTrabajo", "DeleteTrabajo", auth.user, {
                                "Id": work.Id,
                            }).then(() => {
                                props.onWorkDeleted();
                                setWork(undefined);
                                setLoading(false);
                            }, (error: string) => {
                                alert(error);
                                setLoading(false);
                            });
                        }
                    }
                },
            },
        );
    }
    if (workPermission.Modificar) {
        commandBarItems.push(
            {
                key: '4',
                text: 'Registrar parte',
                iconProps: {
                    iconName: 'Add',
                },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para registrar parte.");
                    }
                    else {
                        setIsWorkPartFormOpen(true);
                    }
                },
            },
            {
                key: '5',
                text: 'Adjuntar documento',
                iconProps: {
                    iconName: 'Add',
                },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para adjuntar documento.");
                    }
                    else {
                        setIsWorkDocumentOpen(true);
                    }
                },
            },
            {
                key: '6',
                text: 'Cobros',
                iconProps: { iconName: 'Money' },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para registrar cobros.");
                    } else {
                        setIsCobrosOpen(true);
                    }
                },
            },
            {
                key: '7',
                text: 'Consumos',
                iconProps: { iconName: 'ProductList' },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para ver consumos.");
                    } else {
                        setIsConsumosOpen(true);
                    }
                },
            },
            {
                key: '8',
                text: work?.FechaFinalizacion ? 'Reabrir trabajo' : 'Finalizado',
                iconProps: { iconName: work?.FechaFinalizacion ? 'RevToggleKey' : 'CheckMark' },
                onClick: () => {
                    if (!work) {
                        alert("Seleccione un trabajo para cambiar su estado.");
                        return;
                    }
                    if (auth.user) {
                        setLoading(true);
                        const abrir = !!work.FechaFinalizacion;
                        const params: any = {
                            Id: work.Id,
                            IdOrden: work.IdOrden,
                            FechaFinalizacion: moment().format('YYYY-MM-DD HH:mm:ss'),
                            FormularioDatos: '',
                        };
                        if (abrir) params.Abrir = true;
                        ApiService.Post<string>('OrdenesTrabajo', 'SetTareaFinalizada', auth.user, params)
                            .then(() => {
                                props.onWorkEdited();
                                setLoading(false);
                            }, (err: string) => {
                                alert(err);
                                setLoading(false);
                            });
                    }
                },
            },
        );
    }

    return (
        workPermission.Ver ? (
            <div className='container-fluid p-0'>
                <div className='row m-0'>
                    <div className='col-12'>
                        <CommandBar 
                            items={[]}
                            styles={commandBarStyles} 
                            farItems={commandBarItems} 
                            farItemsGroupAriaLabel="Acciones para los trabajos" />
                    </div>
                    <div className='col-12'>
                        {props.workOrder.ListaTareas.length === 0 ? (
                            <p className={listClasses.emptyMsg}>No tiene actuaciones</p>
                        ) : (
                            <div className={listClasses.container}>
                                {props.workOrder.ListaTareas.map((item: ItemTarea, index: number) => {
                                    const isSelected = work?.Id === item.Id;
                                    const dateStr = item.FechaHoraInicioPrevista
                                        ? item.FechaHoraInicioPrevista.substring(0, 16).replace('T', ' ')
                                        : '';
                                    const sidebarColor = item.FechaFinalizacion
                                        ? '#FF9800'
                                        : '#4CAF50';
                                    return (
                                        <div
                                            key={item.Id || index}
                                            className={isSelected ? listClasses.cardSelected : listClasses.card}
                                            onClick={() => setWork(item)}
                                            onDoubleClick={() => {
                                                if (workPermission.Modificar) {
                                                    setWork(item);
                                                    setIsWorkEditFormOpen(true);
                                                }
                                            }}>
                                            <div className={mergeStyles(listClasses.sidebar, { backgroundColor: sidebarColor })} />
                                            <div className={listClasses.cardInner}>
                                                <div className={listClasses.badge}>
                                                    {item.Id}
                                                </div>
                                                <div className={listClasses.content}>
                                                    <div className={listClasses.title}>
                                                        {item.Descripcion || item.Nombre || '—'}
                                                    </div>
                                                    {(item.Nombre || item.Direccion) && (
                                                        <div className={listClasses.subtitle}>
                                                            {[item.Nombre, item.Direccion].filter(Boolean).join(' · ')}
                                                        </div>
                                                    )}
                                                    {item.Observaciones && (
                                                        <div className={listClasses.obs}>
                                                            {item.Observaciones}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className={listClasses.dateCol}>
                                                    {dateStr}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
                {
                    isWorkCreateFormOpen ? (
                        <WorkCreateComponent
                            workOrder={props.workOrder}
                            onClose={() => setIsWorkCreateFormOpen(false)}
                            onWorkCreated={props.onWorkCreated} />
                    ) : (
                        null
                    )
                }
                {
                    isWorkEditFormOpen && work ? (
                        <WorkEditComponent
                            workOrder={props.workOrder}
                            work={work}
                            onClose={() => setIsWorkEditFormOpen(false)}
                            onWorkEdited={props.onWorkEdited} />
                    ) : (
                        null
                    )
                }
                {
                    isWorkPartFormOpen && work ? (
                        <WorkPartCreateComponent 
                            work={work}
                            onClose={() => setIsWorkPartFormOpen(false)}
                            onWorkPartCreatedOrEdited={props.onWorkPartCreated} />
                    ) : (
                        null
                    )
                }
                {
                    isWorkDocumentOpen && work ? (
                        <WorkDocumentCreateComponent 
                            work={work}
                            onClose={() => setIsWorkDocumentOpen(false)}
                            onWorkDocumentCreated={props.onWorkDocumentCreated} />
                    ) : (
                        null
                    )
                }
                {isCobrosOpen && work ? (
                    <WorkCobrosComponent
                        work={work}
                        onClose={() => setIsCobrosOpen(false)}
                        onSaved={() => props.onWorkEdited()} />
                ) : null}
                {isConsumosOpen && work ? (
                    <WorkConsumosComponent
                        work={work}
                        onClose={() => setIsConsumosOpen(false)} />
                ) : null}                <LoaderComponent loading={loading} />
            </div>
        ) : (
            <NoPermissionComponent showButton={false} />
        )
    );
};

export default WorkOrderWorksComponent;
